import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiKept } from "../api/client";
import type { AudioAssetDto, MappingCandidate, Project, ProposedChapter } from "../api/types";
import { Icon } from "../components/icons";
import { RunPanel } from "../components/RunPanel";
import { ProjectHeader } from "../components/ProjectHeader";
import { ProjectMenu } from "../components/ProjectMenu";
import { ProjectStatusPill } from "../components/ProjectStatusPill";
import { TakesList } from "../components/TakesList";
import { ManuscriptCard } from "../components/ManuscriptCard";
import { ProjectEssentials } from "../components/ProjectEssentials";
import { SubmissionSummary } from "../components/SubmissionSummary";
import { ListenOnPhone } from "../components/ListenOnPhone";
import { SectionHead } from "../components/SectionHead";
import { toast } from "../components/toast";
import { loadState, pendingPickups, tallyReview, type FlagLite, type ReviewTally } from "../lib/reportStore";
import { usePickups } from "../lib/pickups";
import { useReviewState } from "../lib/reviewState";
import { formatTime } from "../lib/format";

interface FlagsResponse {
  chapters: Array<{ chapter_id: string; name: string; order_index: number; flags: FlagLite[] }>;
}

export function ProjectWorkspace() {
  const { projectId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const qc = useQueryClient();
  const [jobId, setJobId] = useState<string | null>(null);
  const [adjustingMapping, setAdjustingMapping] = useState(false);

  // The mega menu's "Chapter mapping" opens the inline editor via ?adjust=mapping
  // (so it works from the report too — it navigates here). Consume + clear it.
  useEffect(() => {
    if (searchParams.get("adjust") === "mapping") {
      setAdjustingMapping(true);
      searchParams.delete("adjust");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const { data: project, refetch } = useQuery({
    queryKey: ["project", projectId],
    queryFn: () => api<Project>(`/api/projects/${projectId}`),
    // Poll while a job is attached OR a background re-proof is running (so the
    // per-file "Re-proofing…" status appears + clears promptly), else idle-poll.
    refetchInterval: (q) =>
      jobId || q.state.data?.active_job?.job_type === "reproof" ? 2500 : 4000,
  });

  // Bind the attached job to whatever is running on the CURRENT project. Keyed
  // on projectId so switching between two running projects re-points the log
  // panel at the right job (rather than keeping the previous project's). The
  // running job's id lives server-side, so navigating away and back reattaches
  // (RunPanel replays the full log + live progress over SSE). A job the user
  // just started doesn't change these deps, so it isn't clobbered before the
  // next poll confirms it.
  useEffect(() => {
    // Only the proofing tab's OWN pipeline jobs drive the full-screen panel. A
    // spec-check (other module) must not replace the proofing view; nor must a
    // background boundary re-proof ("reproof") — that one runs quietly so the
    // reviewer can keep working through the take list while it proofs.
    const aj = project?.active_job;
    const takesOver = aj && aj.module_id === "proofing" && aj.job_type !== "reproof";
    setJobId(takesOver ? aj!.id : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, project?.active_job?.id, project?.active_job?.module_id, project?.active_job?.job_type]);

  // A background boundary re-proof is running on this project (kicked off from a
  // boundary edit). Shown as a slim banner, NOT a takeover — the list stays put.
  const bgReproof =
    project?.active_job?.module_id === "proofing" && project?.active_job?.job_type === "reproof";

  const startJob = useMutation({
    mutationFn: (body: { module_id: string; job_type: string; params?: any }) =>
      api<{ job_id: string }>(`/api/projects/${projectId}/jobs`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: (r) => setJobId(r.job_id),
    onError: (e: any) => toast(e.message || "Could not start job", "error"),
  });

  // Drag-to-reorder the audio-file list (display order only; mapping untouched).
  // Sent as the list shows it, one row per file: the server moves every
  // delivery of each file with it, so a replaced file never blocks a sort.
  const reorder = useMutation({
    mutationFn: (files: string[]) =>
      api(`/api/projects/${projectId}/chapters/reorder`, { method: "POST", body: JSON.stringify({ files }) }),
    // Each file's new place, at once: the list re-reads its order when the
    // project comes back, and must not briefly fall back to the old places.
    onMutate: (files) => {
      qc.setQueryData<AudioAssetDto[]>(["audio", projectId], (old) =>
        old?.map((a) => (files.includes(a.filename) ? { ...a, place: files.indexOf(a.filename) + 1 } : a)));
    },
    onSuccess: () => { refetch(); qc.invalidateQueries({ queryKey: ["projects"] }); },
    onError: (e: any) => { toast(e.message || "Could not reorder.", "error"); refetch(); },
  });

  function onJobDone(status: string) {
    setJobId(null);
    refetch();
    qc.invalidateQueries({ queryKey: ["mapping", projectId] });
    qc.invalidateQueries({ queryKey: ["projects"] });
    qc.invalidateQueries({ queryKey: ["flags", projectId] });
    if (status === "completed") toast("Nice — that's done.", "success");
    else if (status === "failed") toast("That run hit a snag — check the log.", "error");
  }

  // Project-level flag list (compact) for the review-progress summary. Combined
  // with the browser's localStorage review state so picked/dismissed/unreviewed
  // tallies match the report viewer exactly.
  const proofedCount = project?.proofed ?? 0;
  const { data: flagsData } = useQuery({
    // The proof stamp in the key → refetch the moment a file's results land,
    // whoever proofed it (the worker's per-take proofs and background re-proofs
    // aren't surfaced jobs, so onJobDone won't fire for them, and a re-proof
    // leaves the number of proofed files as it was). The page offset shifts
    // every flag's page.
    queryKey: ["flags", projectId, project?.proof_stamp ?? proofedCount, project?.page_offset ?? 0],
    // Kept in the browser and asked for with its ETag: unchanged, the server
    // answers without reading a file, so the column fills as the page loads.
    queryFn: () => apiKept<FlagsResponse>(`/api/modules/proofing/projects/${projectId}/flags`),
    enabled: !!projectId && proofedCount > 0,
    // Every other file keeps its counts while one file's are re-read; never
    // another project's, though, nor any once nothing is proofed.
    placeholderData: (prev, prevQuery) =>
      (proofedCount > 0 && prevQuery?.queryKey[1] === projectId ? prev : undefined),
  });
  // loadState reads localStorage; recompute when the flag set changes (and on
  // remount, e.g. returning from the report after reviewing). One pass yields
  // both the project total and a per-chapter map for the table column.
  const pickups = usePickups(projectId!);
  // Server-synced review state (dismissals/comments/meaning/…) so the per-take
  // ★/✕/• counts reflect work done on ANY device, not just this browser.
  const review = useReviewState(projectId);
  const { tally, reviewByChapter } = useMemo(() => {
    if (!flagsData) return { tally: null as ReviewTally | null, reviewByChapter: {} as Record<string, ReviewTally> };
    const st = review.data ?? loadState(projectId!);
    const per: Record<string, ReviewTally> = {};
    for (const ch of flagsData.chapters) per[ch.chapter_id] = tallyReview(st, [ch], pickups.keys, pickups.list);
    return { tally: tallyReview(st, flagsData.chapters, pickups.keys, pickups.list), reviewByChapter: per };
  }, [flagsData, projectId, pickups.keys, pickups.list, review.data]);

  // Heal the Pickups Package project-wide: push any flag locally marked "picked"
  // that the server is still missing (e.g. picks made while the server-side
  // pickup write was broken). The report only syncs the chapter it's showing, so
  // without this the Package fills in chapter-by-chapter as reports are opened.
  // Idempotent — only pushes what's missing.
  useEffect(() => {
    if (!flagsData || pickups.isLoading || !projectId) return;
    const meta = new Map(
      (project?.chapters ?? []).map((c) => [c.id, { name: c.name, audio_filename: c.audio_filename }])
    );
    for (const snap of pendingPickups(flagsData.chapters, meta, loadState(projectId), pickups.keys)) {
      pickups.pick(snap);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flagsData, pickups.isLoading, projectId]);

  if (!project) return <div className="page muted">Loading…</div>;

  const anyProofed = project.proofed > 0;
  // Files excluded from proofing (bookends/credits) aren't part of the proofing
  // target — drop them from the denominator and call them out separately.
  const excludedCount = project.chapters.filter((c) => c.proof_excluded).length;

  return (
    <>
      <ProjectHeader
        projectId={project.id}
        name={project.name}
        active="hub"
        right={
          <>
            <ProjectMenu project={project} />
            <ProjectStatusPill p={project} tally={tally} pickups={pickups.keys.size} />
            {/* Both shortcuts are Proofing outputs, so they sit together under a
                "Proofing" label — on their own, "Open report" didn't say which
                report and Pickups looked unrelated to anything around it. */}
            {anyProofed && (
              <div className="action-group" role="group" aria-label="Proofing">
                <span className="action-group-label">
                  <Icon name="headphones" size={13} /> Proofing
                </span>
                <Link
                  to={`/projects/${project.id}/report`}
                  className="action-group-btn"
                  title="Open the proofing report to review the flagged differences"
                >
                  <Icon name="file-text" size={15} /> Report
                </Link>
                <button
                  type="button"
                  className="action-group-btn"
                  onClick={() => window.open(`/projects/${project.id}/pickups`, "_blank", "noopener")}
                  title="Open the Pickups Package (picked flags to share or export) in a new tab"
                >
                  <Icon name="mic" size={15} /> Pickups
                  <Icon name="external" size={12} />
                </button>
              </div>
            )}
          </>
        }
      />
      <div className="page">
      <ProjectGlance project={project} tally={tally} excluded={excludedCount}
        pickups={pickups.keys.size} />

      {/* Everything the PM needs for this book — always visible, above the
          job/assets region (which swaps out while a pipeline job runs). */}
      <ProjectEssentials project={project} onChanged={refetch} />

      <ListenOnPhone projectId={project.id} />

      {/* A background boundary re-proof — slim, non-blocking. The take list stays
          below so you can keep reviewing edges while it proofs. */}
      {bgReproof && !jobId && (
        <div className="reproof-hint" role="status">
          <span className="reproof-dot" />
          Re-proofing a chapter in the background — the list below stays usable; it refreshes when done.
        </div>
      )}

      {/* One persistent take list; or the mapping editor when adjusting. A
          user-initiated Re-map / Deep job streams a RunPanel — the background
          per-take worker doesn't (it's excluded from active_job). */}
      {jobId ? (
        <RunPanel key={jobId} jobId={jobId} title="Pipeline" onDone={onJobDone} />
      ) : adjustingMapping ? (
        <>
          <button className="btn ghost" style={{ marginBottom: 10 }} onClick={() => setAdjustingMapping(false)}>
            <Icon name="back" size={15} /> Back
          </button>
          <MappingReview
            projectId={project.id}
            onConfirmed={() => {
              setAdjustingMapping(false);
              refetch();
              qc.invalidateQueries({ queryKey: ["mapping", projectId] });
              qc.invalidateQueries({ queryKey: ["projects"] });
              toast("Mapping updated.", "info");
            }}
            onRepropose={() => {
              setAdjustingMapping(false);
              startJob.mutate({ module_id: "proofing", job_type: "map_propose" });
            }}
          />
        </>
      ) : (
        <>
          {/* ASSETS — drop in what you have; neither blocks the other. */}
          <SectionHead title="Assets" sub="Add a manuscript and/or audio — in any order." />
          <ManuscriptCard
            projectId={project.id}
            filename={project.manuscript_filename}
            onChanged={() => { refetch(); qc.invalidateQueries({ queryKey: ["projects"] }); }}
          />
          <TakesList
            projectId={project.id}
            projectName={project.name}
            hasManuscript={project.has_manuscript}
            chapters={project.chapters}
            reviewByChapter={reviewByChapter}
            proofingActive={project.proofing_active}
            reproofingChapterId={bgReproof ? project.active_job?.chapter_id ?? null : null}
            onReorder={(files) => reorder.mutate(files)}
          />

        </>
      )}

      {/* Last, because it is the end of the job: everything above is the work,
          this is what gets sent when the work is done. */}
      <SubmissionSummary projectId={project.id} />
      </div>
    </>
  );
}

/**
 * The book at a glance.
 *
 * What used to be a grey line of text above Essentials - manuscript, file
 * count, proofed - saying four true things in a shape nobody reads. The same
 * facts, plus the ones a PM actually asks for first (how long is it, how much
 * is left to review), in the card that was already carrying half of them.
 */
function ProjectGlance({ project, tally, excluded, pickups }: {
  project: Project; tally: ReviewTally | null; excluded: number; pickups: number;
}) {
  // Shares the take list's cache, so this costs no extra request.
  const { data: assets } = useQuery({
    queryKey: ["audio", project.id],
    queryFn: () => api<{ assets: AudioAssetDto[] }>(`/api/projects/${project.id}/audio`).then((r) => r.assets),
  });
  const runtime = (assets ?? []).reduce((n, a) => n + (a.duration_seconds || 0), 0);
  const target = Math.max(0, project.n_chapters - excluded);
  const reviewed = tally ? tally.picked + tally.dismissed : 0;
  const pct = tally && tally.total ? Math.round((reviewed / tally.total) * 100) : 0;

  return (
    <div className="card glance">
      <div className="glance-facts">
        <Fact label="Manuscript"
          value={project.has_manuscript ? project.manuscript_filename ?? "—" : "Not added"}
          muted={!project.has_manuscript} mono={project.has_manuscript} />
        <Fact label="Audio" value={project.n_audio ? `${project.n_audio} file${project.n_audio === 1 ? "" : "s"}` : "None yet"}
          muted={!project.n_audio} />
        <Fact label="Runtime" value={runtime ? formatTime(runtime) : "—"} muted={!runtime} mono />
        <Fact label="Proofed" value={target ? `${project.proofed} of ${target}` : "—"}
          muted={!target} />
        {excluded > 0 && <Fact label="Excluded" value={`${excluded} file${excluded === 1 ? "" : "s"}`} />}
        {pickups > 0 && <Fact label="Pickups" value={String(pickups)} />}
      </div>

      {tally && tally.total > 0 && (
        <div className="glance-review">
          <FlagStats tally={tally} reviewed={reviewed} pct={pct} />
        </div>
      )}
    </div>
  );
}

function Fact({ label, value, muted, mono }: {
  label: string; value: string; muted?: boolean; mono?: boolean;
}) {
  return (
    <span className="glance-fact">
      <span className="glance-k">{label}</span>
      <span className={`glance-v${muted ? " muted" : ""}${mono ? " mono" : ""}`} title={value}>{value}</span>
    </span>
  );
}

function FlagStats({ tally, reviewed, pct }: { tally: ReviewTally; reviewed: number; pct: number }) {
  return (
    <>
      <div className="fs-head">
        <h3>Review progress</h3>
        <span className="muted" style={{ fontSize: 12.5 }}>
          {reviewed}/{tally.total} reviewed · {pct}%
        </span>
      </div>
      <div className="fs-pills">
        <span className="fs-pill picked"><span className="fs-ic">★</span><b>{tally.picked}</b> picked</span>
        <span className="fs-pill dismissed"><span className="fs-ic">✕</span><b>{tally.dismissed}</b> dismissed</span>
        <span className="fs-pill unreviewed"><span className="fs-ic">•</span><b>{tally.unreviewed}</b> unreviewed</span>
        {tally.filtered > 0 && (
          <span className="fs-pill muted" title="Auto-filtered non-issues (punctuation, number wording, homophones) — not counted in the review total">
            <b>{tally.filtered}</b> auto-filtered
          </span>
        )}
      </div>
      <div className="fs-bar" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <span className="fs-bar-picked" style={{ width: `${tally.total ? (tally.picked / tally.total) * 100 : 0}%` }} />
        <span className="fs-bar-dismissed" style={{ width: `${tally.total ? (tally.dismissed / tally.total) * 100 : 0}%` }} />
      </div>
    </>
  );
}

const REASON_TEXT: Record<string, string> = {
  low_head_score: "Weak content match at the start",
  low_tail_score: "Couldn't confirm where it ends",
  gap_before_next: "Gap before the next chapter — possible extra/missing pages",
  overlap_with_next: "Overlaps the next chapter",
  short_chapter: "Unusually short for a chapter",
  name_order_conflict: "Filename order differs from content order",
  no_transcript: "No transcript for this audio file",
  llm_relocated: "AI moved the start — please confirm",
  llm_uncertain: "AI wasn't sure about the start",
  empty_manuscript: "Manuscript has no readable text",
};

function MappingReview({
  projectId, onConfirmed, onRepropose,
}: { projectId: string; onConfirmed: () => void; onRepropose: () => void }) {
  const [rows, setRows] = useState<ProposedChapter[]>([]);
  const { data } = useQuery({
    queryKey: ["mapping", projectId],
    queryFn: () => api<{ proposed: ProposedChapter[] | null }>(
      `/api/modules/proofing/projects/${projectId}/mapping`
    ).then((r) => r.proposed || []),
  });
  useEffect(() => { if (data) setRows(data); }, [data]);

  const confirm = useMutation({
    mutationFn: () =>
      api(`/api/modules/proofing/projects/${projectId}/mapping/confirm`, {
        method: "POST",
        body: JSON.stringify({ chapters: rows }),
      }),
    onSuccess: () => { toast("Chapters are ready.", "success"); onConfirmed(); },
    onError: (e: any) => toast(e.message || "We couldn't build the chapters.", "error"),
  });

  function patchRow(i: number, patch: Partial<ProposedChapter>) {
    setRows((cur) => cur.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  }
  function setPage(i: number, which: 0 | 1, val: string) {
    const n = Number(val) || null;
    setRows((cur) => cur.map((r, j) =>
      j === i ? { ...r, pages: which === 0 ? [n, r.pages[1]] : [r.pages[0], n] } : r
    ));
  }
  function pickCandidate(i: number, c: MappingCandidate) {
    patchRow(i, {
      start_token_index: c.token_index,
      start_page: c.page,
      pages: [c.page ?? rows[i].pages[0], rows[i].pages[1]],
      heading: c.heading ?? rows[i].heading,
      manuscript_start_excerpt: c.snippet ?? rows[i].manuscript_start_excerpt,
    });
  }

  const items = rows.map((r, i) => ({ r, i }));
  const exceptions = items.filter((x) => !x.r.auto);
  const confident = items.filter((x) => x.r.auto);
  const allAuto = rows.length > 0 && exceptions.length === 0;

  const confirmBtn = (
    <button className="btn primary" disabled={confirm.isPending || rows.length === 0}
      onClick={() => confirm.mutate()}>
      {allAuto ? "Looks good → build chapters" : "Confirm & build chapters"}
    </button>
  );

  return (
    <div className="map-review">
      <div className="card map-summary">
        <div>
          <h3>Review proposed mapping</h3>
          <div className="muted" style={{ fontSize: 12.5 }}>
            {rows.length} chapters · <b className="ok-text">{confident.length} auto-mapped</b>
            {exceptions.length > 0 && <> · <b className="warn-text">{exceptions.length} need a look</b></>}
          </div>
        </div>
        <div className="row">
          <button className="btn" onClick={onRepropose}>Re-propose</button>
          {confirmBtn}
        </div>
      </div>

      {allAuto && (
        <div className="card map-allgood">
          <div className="ag-check">✓</div>
          <div>
            <strong>All {rows.length} chapters mapped confidently.</strong>
            <div className="muted" style={{ fontSize: 12.5 }}>
              Each chapter's start and end were matched against the manuscript. Build them, or expand below to double-check.
            </div>
          </div>
        </div>
      )}

      {exceptions.map(({ r, i }) => (
        <ExceptionCard
          key={i}
          projectId={projectId}
          index={i}
          r={r}
          onPick={(c) => pickCandidate(i, c)}
          onSetPage={(which, val) => setPage(i, which, val)}
        />
      ))}

      {confident.length > 0 && (
        <details className="card map-confident" open={allAuto}>
          <summary>
            {allAuto ? "All chapters" : `${confident.length} auto-mapped chapters`} — review or edit pages
          </summary>
          <table className="table">
            <thead>
              <tr><th>#</th><th>Chapter</th><th>Audio</th><th>First</th><th>Last</th><th>Match</th></tr>
            </thead>
            <tbody>
              {confident.map(({ r, i }) => (
                <tr key={i}>
                  <td className="muted">{i + 1}</td>
                  <td className="cellname">{r.heading || r.name}</td>
                  <td className="mono" style={{ fontSize: 12 }}>{r.audio_basename}</td>
                  <td><input className="input sm-input" value={r.pages[0] ?? ""} onChange={(e) => setPage(i, 0, e.target.value)} /></td>
                  <td><input className="input sm-input" value={r.pages[1] ?? ""} onChange={(e) => setPage(i, 1, e.target.value)} /></td>
                  <td>
                    {r.confidence != null
                      ? <span className="badge ok">{Math.round(r.confidence * 100)}%</span>
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}

      <div className="row" style={{ justifyContent: "flex-end" }}>{confirmBtn}</div>
    </div>
  );
}

function ExceptionCard({
  projectId, index, r, onPick, onSetPage,
}: {
  projectId: string;
  index: number;
  r: ProposedChapter;
  onPick: (c: MappingCandidate) => void;
  onSetPage: (which: 0 | 1, val: string) => void;
}) {
  const [showPage, setShowPage] = useState(false);
  const candidates = r.candidates || [];
  const conf = r.confidence != null ? Math.round(r.confidence * 100) : null;

  return (
    <div className="card exc-card">
      <div className="exc-head">
        <div className="row" style={{ gap: 8, alignItems: "baseline" }}>
          <span className="exc-num">{index + 1}</span>
          <strong>{r.heading || r.name}</strong>
          <span className="mono muted" style={{ fontSize: 12 }}>{r.audio_basename}</span>
        </div>
        <div className="row" style={{ gap: 6 }}>
          {r.llm_resolved && (
            <span className="ai-badge" title={r.llm_rationale || "AI reviewed"}>✦ AI</span>
          )}
          {conf != null && <span className={`badge ${conf < 30 ? "bad" : "warn"}`}>{conf}% match</span>}
        </div>
      </div>

      {(r.reasons || []).length > 0 && (
        <div className="exc-reasons">
          {(r.reasons || []).map((code) => (
            <span key={code} className="reason-chip" title={code}>{REASON_TEXT[code] || code}</span>
          ))}
        </div>
      )}

      <div className="exc-grid">
        <div className="exc-col">
          <div className="exc-label">Manuscript starts here {r.start_page != null && <span className="muted">· p.{r.start_page}</span>}</div>
          <div className="exc-excerpt ms">{r.manuscript_start_excerpt || <span className="muted">—</span>}</div>
        </div>
        <div className="exc-col">
          <div className="exc-label">Narrator opens with</div>
          <div className="exc-excerpt audio">{r.audio_head_excerpt || <span className="muted">—</span>}</div>
        </div>
      </div>

      {candidates.length > 1 && (
        <div className="exc-candidates">
          <div className="exc-label">Where does this chapter start?</div>
          {candidates.map((c, k) => {
            const selected = c.token_index === r.start_token_index;
            return (
              <label key={k} className={`cand ${selected ? "on" : ""}`}>
                <input type="radio" name={`cand-${index}`} checked={selected} onChange={() => onPick(c)} />
                <span className="cand-page">p.{c.page ?? "?"}</span>
                <span className="cand-text">
                  {c.heading && <b>{c.heading}: </b>}
                  <span className="muted">{c.snippet}</span>
                </span>
              </label>
            );
          })}
        </div>
      )}

      <div className="exc-foot">
        <div className="row" style={{ gap: 8, alignItems: "center" }}>
          <label className="exc-pages">First <input className="input sm-input" value={r.pages[0] ?? ""} onChange={(e) => onSetPage(0, e.target.value)} /></label>
          <label className="exc-pages">Last <input className="input sm-input" value={r.pages[1] ?? ""} onChange={(e) => onSetPage(1, e.target.value)} /></label>
        </div>
        {r.start_page != null && (
          <button className="btn sm" onClick={() => setShowPage((v) => !v)}>
            {showPage ? "Hide page" : "Show the page"}
          </button>
        )}
      </div>

      {showPage && r.start_page != null && (
        <div className="exc-pdf">
          <embed
            src={`/api/projects/${projectId}/manuscript.pdf#page=${r.start_page}`}
            type="application/pdf"
            title={`Manuscript page ${r.start_page}`}
          />
          <div className="muted" style={{ fontSize: 11.5, padding: "4px 2px" }}>
            Showing physical page {r.start_page}. (Some browsers open the PDF at the top — scroll to the page if needed.)
          </div>
        </div>
      )}
    </div>
  );
}
